import { CheckBadgeIcon } from "@heroicons/react/20/solid";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import { useRouter } from "router2";

import { useRelatedPokeList } from "~/hook/domain/useRelatedPokeList.ts";
import { DELETED_USER } from "~/service/const.ts";
import { isVerifiedUser } from "~/service/util.ts";
import { Image } from "~/ui/base/Image.tsx";
import { Stat } from "~/ui/base/Stat.tsx";
import { PokeListItem } from "~/ui/domain/PokeListItem.tsx";
import { WebLayout } from "~/ui/layout/WebLayout.tsx";
import { WebSharedProfileSheet } from "~/ui/overlay/WebSharedProfileSheet.tsx";
import { WebUpdateMyInfoStack } from "~/ui/overlay/WebUpdateMyInfoStack.tsx";
import { useAuthNavigator, useUser } from "~/ui/provider/Auth.tsx";
import { useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const cx = {
  actionButton:
    "flex-1 rounded-xl border border-zinc-200 dark:border-zinc-800 px-2 py-1 text-sm font-medium text-zinc-800 dark:text-zinc-200 duration-300 active:opacity-60",
};

export const WebMyPage = () => {
  useAuthNavigator({ goToAuth: "/web/sign-in" });
  const { replace } = useRouter();
  const overlay = useStackedLayer();
  const { t } = useLingui();
  const { myInfo } = useUser({ revalidateIfHasToken: true });

  const { data, intersectorRef, isFreshData } = useRelatedPokeList();

  return (
    <WebLayout>
      <div className="mx-auto max-w-2xl">
        <div className="flex pt-2">
          <Image
            alt={t`프로필 이미지`}
            size={80}
            src={myInfo?.profileImageUrl ?? "/asset/default_user_profile.png"}
            transitionName="my-profile"
          />
          <ul className="grid flex-1 grid-cols-3 items-center pl-5">
            <li>
              <Stat
                label={t`모든 콕!`}
                value={myInfo ? myInfo.pokes + myInfo.pokeds : undefined}
              />
            </li>
            <li>
              <Stat label={t`내가 콕!`} value={myInfo?.pokes} />
            </li>
            <li>
              <Stat label={t`나를 콕!`} value={myInfo?.pokeds} />
            </li>
          </ul>
        </div>
        <div className="pt-7">
          <h1 className="flex items-center text-xl font-bold">
            <span className="min-w-0 wrap-anywhere">@{myInfo?.email}</span>
            {myInfo && isVerifiedUser(myInfo) && (
              <span className="ml-1 text-blue-500">
                <CheckBadgeIcon className="size-5" />
              </span>
            )}
          </h1>
          <p className="mt-1">{myInfo?.name ?? <div className="h-6" />}</p>
        </div>
        <div className="flex gap-2 pt-5">
          <button
            className={cx.actionButton}
            onClick={() => {
              overlay(WebUpdateMyInfoStack);
            }}
            type="button"
          >
            <Trans>프로필 편집</Trans>
          </button>
          <button
            className={cx.actionButton}
            onClick={() => {
              overlay(WebSharedProfileSheet);
            }}
            type="button"
          >
            <Trans>프로필 공유</Trans>
          </button>
        </div>

        <ul className="mt-10 flex flex-col gap-4" data-testid="콕찌르기 기록">
          {data?.[0].length === 0 && (
            <li className="animate-from-bottom flex flex-col items-center pt-10 text-zinc-700 dark:text-zinc-400">
              <SparklesIcon className="size-6" />
              <p className="pt-6">
                <Trans>처음으로 콕 찔러보세요!</Trans>
              </p>
              <button
                className="mt-12 rounded-full bg-black p-3 text-white active:opacity-60 disabled:bg-zinc-300 dark:bg-zinc-800 dark:disabled:bg-zinc-900"
                onClick={() => {
                  replace({ pathname: "/web/search" });
                }}
              >
                <Trans>콕 찌르기</Trans>
                {" 👉"}
              </button>
            </li>
          )}
          {data?.flatMap((pokes, pageIndex) =>
            pokes.map(
              (
                {
                  createdAt,
                  fromUserId,
                  id,
                  payload,
                  relation: { fromUser, toUser },
                },
                index,
              ) => {
                const type = fromUserId === myInfo?.id ? "poke" : "poked";
                const targetUser = {
                  poke: toUser,
                  poked: fromUser,
                }[type];
                const animation = isFreshData(pageIndex)
                  ? { delayTimes: index }
                  : null;
                const isVerified = targetUser
                  ? isVerifiedUser(targetUser)
                  : false;
                return (
                  <PokeListItem
                    animation={animation}
                    date={createdAt}
                    id={id}
                    isVerifiedUser={isVerified}
                    key={id}
                    payload={payload}
                    targetUser={targetUser ?? DELETED_USER}
                    type={type}
                  />
                );
              },
            ),
          )}
          <div className="h-24" ref={intersectorRef}></div>
        </ul>
      </div>
    </WebLayout>
  );
};
